import { useState, useEffect, useCallback } from 'react';

export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

export interface PaginationActions {
  setPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  setPageSize: (size: number) => void;
  reset: () => void;
  setTotalItems: (total: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export interface UsePaginationOptions {
  initialPageSize?: number;
  enableLogging?: boolean;
}

export function usePagination(options: UsePaginationOptions = {}) {
  const {
    initialPageSize = 20,
    enableLogging = false
  } = options;

  const [state, setState] = useState<PaginationState>({
    currentPage: 1,
    pageSize: initialPageSize,
    totalItems: 0,
    totalPages: 0,
    isLoading: false,
    error: null
  });

  const log = useCallback((message: string, data?: any) => {
    if (enableLogging) {
      console.log(`[Pagination] ${message}`, data);
    }
  }, [enableLogging]);

  // Calculate total pages when totalItems or pageSize changes
  useEffect(() => {
    const totalPages = Math.ceil(state.totalItems / state.pageSize);
    setState(prev => ({ ...prev, totalPages }));
    log('Total pages calculated', { totalItems: state.totalItems, pageSize: state.pageSize, totalPages });
  }, [state.totalItems, state.pageSize, log]);


  const actions: PaginationActions = {
    setPage: useCallback((page: number) => {
      setState(prev => ({
        ...prev,
        currentPage: Math.max(1, Math.min(page, prev.totalPages || 1)),
        error: null
      }));
    }, []),

    nextPage: useCallback(() => {
      setState(prev => ({
        ...prev,
        currentPage: Math.min(prev.currentPage + 1, prev.totalPages || 1),
        error: null
      }));
    }, []),

    prevPage: useCallback(() => {
      setState(prev => ({
        ...prev,
        currentPage: Math.max(prev.currentPage - 1, 1),
        error: null
      }));
    }, []),

    setPageSize: useCallback((size: number) => {
      setState(prev => ({
        ...prev,
        pageSize: Math.max(1, size),
        currentPage: 1, // Reset to first page when changing page size
        error: null
      }));
    }, []),

    reset: useCallback(() => {
      setState({
        currentPage: 1,
        pageSize: initialPageSize,
        totalItems: 0,
        totalPages: 0,
        isLoading: false,
        error: null
      });
      log('Pagination reset');
    }, [initialPageSize, log]),

    setTotalItems: useCallback((total: number) => {
      setState(prev => ({ ...prev, totalItems: Math.max(0, total) }));
    }, []),

    setLoading: useCallback((loading: boolean) => {
      setState(prev => ({ ...prev, isLoading: loading }));
    }, []),

    setError: useCallback((error: string | null) => {
      setState(prev => ({ ...prev, error, isLoading: false }));
    }, [])
  };

  return {
    ...state,
    ...actions,
    // Computed values
    hasNextPage: state.currentPage < state.totalPages,
    hasPrevPage: state.currentPage > 1,
    startIndex: (state.currentPage - 1) * state.pageSize,
    endIndex: Math.min(state.currentPage * state.pageSize, state.totalItems),
    // Helper for API calls
    getOffset: () => (state.currentPage - 1) * state.pageSize,
    getLimit: () => state.pageSize
  };
}